import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { isNil } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { ABNInput } from '@paid-ui/components/abn-input';
import { Button } from '@paid-ui/components/button';
import { FormControl, FormField, FormHelper, FormItem } from '@paid-ui/components/form';
import { InlineWarning } from '@paid-ui/components/inline-warning';
import { RadioGroup, RadioGroupCard } from '@paid-ui/components/radio-group';
import { BusinessStructure } from '@paid-ui/enums/business';
import { useBusinessABNCheck } from '@paid-ui/queries/business-abn-check';

import { signUpManager } from '../../models';
import { SignUpStep } from '../../models/enums';

const WITH_ABN = 'with_abn';
const WITHOUT_ABN = 'without_abn';
type AbnOption = typeof WITH_ABN | typeof WITHOUT_ABN;

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep) => void;
}

export const StepBusinessABN: React.FC<Props> = ({ disabled, goToStep }) => {
  const router = useRouter();
  const form = useFormContext();
  const { currentStep, userDeclaredNoAbn } = useSnapshot(signUpManager);
  const [abn, setABN] = useState('');
  const [acknowledgedAbn, setAcknowledgedAbn] = useState<string | null>(null);
  const { data, refetch, isFetching } = useBusinessABNCheck({ abn });

  const structure = form.watch('structure');
  const _abn = form.watch('abn');

  let abnOption: AbnOption | '' = '';
  if (userDeclaredNoAbn === true) {
    abnOption = WITHOUT_ABN;
  } else if (userDeclaredNoAbn === false) {
    abnOption = WITH_ABN;
  }

  const isValid =
    abnOption === WITHOUT_ABN ||
    (abnOption === WITH_ABN && Boolean(_abn) && !form.getFieldState('abn').invalid);
  const showWarning = data === true && _abn === acknowledgedAbn;

  const handleOptionChange = (value: string) => {
    const option = value as AbnOption;
    signUpManager.userDeclaredNoAbn = option === WITHOUT_ABN;
    if (option === WITHOUT_ABN) {
      form.setValue('abn', '', { shouldValidate: true });
    }
  };

  const proceed = () => {
    switch (structure) {
      case BusinessStructure.COMPANY: {
        goToStep?.(SignUpStep.BUSINESS_COMPANY_DETAILS);
        break;
      }
      case BusinessStructure.PARTNERSHIP:
      case BusinessStructure.SOLE_TRADER:
      case BusinessStructure.OTHER: {
        goToStep?.(SignUpStep.BUSINESS_DETAILS);
        break;
      }
      case BusinessStructure.TRUST: {
        goToStep?.(SignUpStep.TRUSTEE_DETAILS);
        break;
      }
      default:
    }
  };

  const handleNext = () => {
    const currentAbn = form.getValues('abn');
    if (!currentAbn) {
      proceed();
      return;
    }
    if (acknowledgedAbn === currentAbn) {
      proceed();
      return;
    }
    setABN(currentAbn);
    form.register('abn');
    setTimeout(refetch, 100);
  };

  const handleSignIn = () => {
    const urlSearchParams = new URLSearchParams(globalThis.location.search);
    urlSearchParams.delete('step');
    router.push({
      pathname: '/signin',
      search: urlSearchParams.toString(),
    });
  };

  useEffect(() => {
    if (isNil(data)) {
      return;
    }

    if (data) {
      setAcknowledgedAbn(abn);
    } else {
      proceed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching]);

  useEffect(() => {
    if (currentStep === SignUpStep.BUSINESS_ABN) {
      setAcknowledgedAbn(null);
    }
  }, [currentStep]);

  if (currentStep !== SignUpStep.BUSINESS_ABN) {
    return null;
  }

  return (
    <div className="space-y-9">
      <div className="space-y-3.5">
        <RadioGroup
          value={abnOption}
          onValueChange={handleOptionChange}
          disabled={disabled}
          className="space-y-3.5"
        >
          <RadioGroupCard value={WITH_ABN}>Yes, I have an ABN</RadioGroupCard>
          <RadioGroupCard value={WITHOUT_ABN}>No, I don't have an ABN</RadioGroupCard>
        </RadioGroup>
        {abnOption === WITH_ABN ? (
          <FormField
            name="abn"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ABNInput {...field} placeholder="My ABN" disabled={disabled} />
                </FormControl>
                <FormHelper />
              </FormItem>
            )}
          />
        ) : null}
      </div>
      <InlineWarning hidden={!showWarning}>
        This ABN is already registered. You can continue signup or sign in instead.
      </InlineWarning>
      <div className="flex gap-3">
        <Button
          variant="solid"
          disabled={disabled || !isValid}
          loading={isFetching}
          className="h-[44px]"
          onClick={handleNext}
        >
          {isFetching ? 'Checking...' : 'Continue'}
        </Button>
        {showWarning ? (
          <Button variant="outline" disabled={disabled} className="h-[44px]" onClick={handleSignIn}>
            Sign in
          </Button>
        ) : null}
      </div>
    </div>
  );
};
